import { styled } from 'styled-components'

import {
  CurrencyStringCents,
  FormattedMessage,
  Radio,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import { QuoteRating } from '@b-stock/shipment-api-client'

import { getServiceLevel } from './groupParcelRates'
import type { ParcelQuote } from './types'

const ns = 'ParcelRateList'

const OptionContainer = styled.div<{
  $isSelected?: boolean
  $disabled?: boolean
}>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border: 0.0625rem solid
    ${({ $isSelected }) =>
      $isSelected
        ? Colors.Semantic.BStock.onhover
        : Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  /* FP-1910: cards are interactive — clicking selects the rate. */
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  margin-bottom: 0.75rem;

  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled ? undefined : Colors.Semantic.BStock.onhover};
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.125rem;
`

const Content = styled.div`
  flex: 1;
  min-width: 0;
`

const TopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`

const ServiceName = styled.div`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const Price = styled.div`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
  white-space: nowrap;
`

const Transit = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  margin-top: 0.25rem;
`

export const RateBadge = styled.span`
  ${Typography.Body4}
  display: inline-block;
  background-color: ${Colors.Semantic.SpecificFunctions.Tags.success};
  color: ${Colors.Semantic.Success.copy};
  border-radius: 0.25rem;
  padding: 0.125rem 0.5rem;
  font-weight: bold;
  text-transform: uppercase;
`

type ParcelRateCardProps = {
  quote: ParcelQuote
  isSelected: boolean
  currency: string
  /** Disables interaction (e.g. while a sibling selection is in flight). */
  disabled?: boolean
  /** Fired with the quote id when the card or its radio is chosen. */
  onSelect?: (quoteId: string) => void
}

export const ParcelRateCard = ({
  quote,
  isSelected,
  currency,
  disabled = false,
  onSelect,
}: ParcelRateCardProps) => {
  const serviceLevel = getServiceLevel(quote)
  const businessDays = quote.estimatedDeliveryDuration?.businessDays
  const isCheapest = quote.rating === QuoteRating.Cheapest
  const isGuaranteed = quote.deliveryGuaranteed === true
  const quoteId = quote._id ?? ''

  const handleSelect = () => {
    if (disabled || quoteId === '') return
    onSelect?.(quoteId)
  }

  return (
    <OptionContainer
      $isSelected={isSelected}
      $disabled={disabled}
      data-testid="parcel-rate-card"
      onClick={handleSelect}
    >
      {/* The Radio renders its own <label>+input, so a mouse click there also
          bubbles to OptionContainer. Stop the click here so selection on the
          radio is driven solely by its onChange (which also covers keyboard),
          preventing a double onSelect. */}
      <RadioContainer onClick={(e) => e.stopPropagation()}>
        <Radio
          size={14}
          name="parcelRate"
          value={quoteId}
          checked={isSelected}
          disabled={disabled}
          onChange={handleSelect}
        />
      </RadioContainer>
      <Content>
        <TopRow>
          <ServiceName>
            {serviceLevel}
            {isCheapest && (
              <RateBadge>
                <FormattedMessage id={`${ns}.badge.cheapest`} />
              </RateBadge>
            )}
            {isGuaranteed && (
              <RateBadge>
                <FormattedMessage id={`${ns}.badge.guaranteed`} />
              </RateBadge>
            )}
          </ServiceName>
          <Price>
            <CurrencyStringCents value={quote.totalPrice} currency={currency} />
          </Price>
        </TopRow>
        {businessDays != null && (
          <Transit>
            <FormattedMessage
              id={`${ns}.transit`}
              values={{ days: businessDays }}
            />
          </Transit>
        )}
      </Content>
    </OptionContainer>
  )
}
